/* Nodus Mosaic Grid
   Tablet and desktop keep the reference mosaic.
   Top row weights are the wide Warmie card plus two equal cards.
   Bottom row weights are the six measured card widths.
   Mobile is a swipeable slider. The first slide keeps the wide first card
   above the next two. The next slide is 2 by 2. The last step shifts one
   column: the previous right column stays on the left, and the final pair
   arrives on the right at single-card width. */

if (!customElements.get('nodus-mosaic-grid')) {
  const TOP_WEIGHTS = [440, 260, 260];
  const BOTTOM_WEIGHTS = [148, 165, 152, 150, 143, 187];
  const BOTTOM_PER_ROW = 6;

  class NodusMosaicGrid extends HTMLElement {
    #stage = null;
    #observer = null;
    #frame = 0;
    #force = false;
    #lastWidth = -1;
    #lastMode = '';
    #onLoad = null;
    #onResize = null;
    #onScroll = null;
    #onBlockSelect = null;
    #dots = null;
    #snapPoints = [];

    connectedCallback() {
      this.#stage = this.querySelector('[data-mosaic-stage]');

      if (!this.#stage) return;

      this.#layout(true);

      this.#observer = new ResizeObserver(() => {
        this.#schedule();
      });
      this.#observer.observe(this.#stage);

      this.#onResize = () => this.#schedule();
      window.addEventListener('resize', this.#onResize);

      this.#onScroll = () => {
        if (!this.#stage || this.#mode() !== 'mobile') return;
        this.#setActiveDot(this.#currentSlide());
      };
      this.#stage.addEventListener('scroll', this.#onScroll, { passive: true });

      this.#onBlockSelect = (event) => {
        if (this.#mode() !== 'mobile' || !this.#stage) return;
        const card = event.target?.closest?.('[data-mosaic-card]');
        if (!card || !this.contains(card)) return;

        const left = this.#cardStageLeft(card);
        const view = this.#stage.clientWidth || 1;
        const index = this.#snapPoints.findIndex((point) => left >= point - 1 && left < point + view - 1);
        if (index >= 0) this.#scrollToSlide(index);
      };
      this.addEventListener('shopify:block:select', this.#onBlockSelect);

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this.#schedule(true);
        });
      }

      if (document.readyState !== 'complete') {
        this.#onLoad = () => {
          if (this.isConnected) this.#schedule(true);
        };
        window.addEventListener('load', this.#onLoad, { once: true });
      }
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#observer = null;

      if (this.#frame) {
        cancelAnimationFrame(this.#frame);
        this.#frame = 0;
      }

      if (this.#onLoad) {
        window.removeEventListener('load', this.#onLoad);
        this.#onLoad = null;
      }

      if (this.#onResize) {
        window.removeEventListener('resize', this.#onResize);
        this.#onResize = null;
      }

      if (this.#onScroll && this.#stage) {
        this.#stage.removeEventListener('scroll', this.#onScroll);
      }
      this.#onScroll = null;

      if (this.#onBlockSelect) {
        this.removeEventListener('shopify:block:select', this.#onBlockSelect);
        this.#onBlockSelect = null;
      }
    }

    #schedule(force = false) {
      if (force) this.#force = true;
      if (this.#frame) return;

      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        const forceLayout = this.#force;
        this.#force = false;
        this.#layout(forceLayout);
      });
    }

    #number(name, fallback) {
      const value = parseFloat(this.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    #gapValue(styles) {
      const value = parseFloat(styles.rowGap || styles.gap);
      return Number.isFinite(value) ? value : 0;
    }

    #mode() {
      return window.innerWidth < this.#number('breakpoint', 975) ? 'mobile' : 'mosaic';
    }

    #clampGap(width, gap, columns) {
      const maxGap = Math.max(0, Math.floor(width / Math.max(columns * 4, 1)));
      return Math.max(0, Math.min(gap, maxGap));
    }

    #distribute(weights, total, gap) {
      const count = weights.length;

      if (!count || total <= 0) return [];

      const available = Math.max(0, total - gap * Math.max(0, count - 1));
      const sum = weights.reduce((totalWeight, weight) => totalWeight + weight, 0) || count;
      const widths = [];
      let used = 0;

      for (let index = 0; index < count; index += 1) {
        if (index === count - 1) {
          widths.push(Math.max(0, available - used));
        } else {
          const width = Math.round((available * weights[index]) / sum);
          widths.push(width);
          used += width;
        }
      }

      return widths;
    }

    #chunk(items, size) {
      const rows = [];

      for (let index = 0; index < items.length; index += size) {
        rows.push(items.slice(index, index + size));
      }

      return rows;
    }

    #labelHeight(card) {
      const label = card.querySelector('.nmg__label');
      if (!label) return 0;

      label.style.height = 'auto';
      return label.offsetHeight;
    }

    #fitHeight(cards, widths, baseHeight) {
      cards.forEach((card, index) => {
        card.style.width = `${widths[index]}px`;
      });

      let labelBand = 0;

      cards.forEach((card) => {
        labelBand = Math.max(labelBand, this.#labelHeight(card));
      });

      const sample = cards[0];
      const styles = sample ? getComputedStyle(sample) : null;
      const pad = styles ? parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom) : 0;
      const columnGap = styles ? this.#gapValue(styles) : 0;
      const minMedia = Math.round(baseHeight * 0.46);
      const needed = Math.ceil(labelBand + pad + (labelBand ? columnGap : 0) + minMedia);

      return {
        height: Math.max(baseHeight, needed),
        labelBand
      };
    }

    #place(card, x, y, width, height, labelBand) {
      const right = x + width;
      const stageWidth = Math.round(this.#stage.clientWidth);
      const safeWidth = right > stageWidth ? Math.max(0, width - (right - stageWidth)) : width;

      card.style.width = `${safeWidth}px`;
      card.style.height = `${height}px`;
      card.style.left = `${x}px`;
      card.style.top = `${y}px`;

      const label = card.querySelector('.nmg__label');
      if (label) label.style.height = `${labelBand}px`;

      const media = card.querySelector('.nmg__media');
      if (!media) return;

      const styles = getComputedStyle(card);
      const pad = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      const columnGap = label ? this.#gapValue(styles) : 0;
      const mediaHeight = Math.max(0, height - pad - labelBand - columnGap);
      media.style.height = `${mediaHeight}px`;
      this.#frameImages(card);
    }

    #cardNumber(card, name, fallback) {
      const value = parseFloat(card.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    #frameImages(card) {
      const media = card.querySelector('.nmg__media');
      const images = card.querySelectorAll('.nmg__img');
      if (!media || !images.length) return;

      const top = this.#cardNumber(card, 'imgTop', 0);
      const right = this.#cardNumber(card, 'imgRight', 0);
      const bottom = this.#cardNumber(card, 'imgBottom', 0);
      const left = this.#cardNumber(card, 'imgLeft', 0);
      const frameWidth = Math.max(0, media.clientWidth - left - right);
      const frameHeight = Math.max(0, media.clientHeight - top - bottom);
      let originX = 'center';
      let originY = 'center';

      if (left < right) originX = 'left';
      else if (right < left) originX = 'right';

      if (bottom < top) originY = 'bottom';
      else if (top < bottom) originY = 'top';

      const origin = `${originX} ${originY}`;

      images.forEach((img) => {
        img.style.top = `${top}px`;
        img.style.right = 'auto';
        img.style.bottom = 'auto';
        img.style.left = `${left}px`;
        img.style.width = `${frameWidth}px`;
        img.style.height = `${frameHeight}px`;
        img.style.objectPosition = origin;
        img.style.transformOrigin = origin;
      });
    }

    #placeRow(cards, widths, y, height, labelBand, gap) {
      let x = 0;

      cards.forEach((card, index) => {
        this.#place(card, x, y, widths[index], height, labelBand);
        x += widths[index] + gap;
      });
    }

    #layout(force = false) {
      if (!this.#stage || !this.isConnected) return;

      const width = Math.round(this.#stage.clientWidth);
      const mode = this.#mode();

      if (width < 1) return;
      if (!force && width === this.#lastWidth && mode === this.#lastMode) return;

      const cards = [...this.#stage.querySelectorAll('[data-mosaic-card]')];
      this.#stage.classList.add('is-ready');
      this.#stage.dataset.layout = mode;

      if (!cards.length) {
        this.#flattenSlides();
        this.#stage.style.height = '0px';
        this.#lastWidth = width;
        this.#lastMode = mode;
        return;
      }

      if (mode === 'mobile') {
        this.#layoutMobile(cards, width);
      } else {
        this.#flattenSlides();
        this.#layoutMosaic(cards, width);
      }

      this.#lastWidth = width;
      this.#lastMode = mode;
    }

    #mobilePlan(cards, width, gap, cardWidth) {
      const slides = [];
      const snaps = [];
      if (!cards.length) return { slides, snaps };

      const remainder = Math.max(0, width - (cardWidth * 2 + gap));
      let group = 0;
      let offset = 0;

      const pushSlide = (slide) => {
        slides.push(slide);
        if (slide.snap) snaps.push(offset);
        offset += slide.width + gap;
      };

      pushSlide({
        role: 'hero',
        group,
        width,
        marginLeft: 0,
        snap: true,
        rows: [
          { cards: [cards[0]], span: 'full' },
          ...(cards.length > 1
            ? [{ cards: cards.slice(1, Math.min(3, cards.length)), span: 'half' }]
            : [])
        ]
      });
      group += 1;

      let index = Math.min(3, cards.length);

      while (index < cards.length) {
        const remaining = cards.length - index;

        if (remaining > 4) {
          const page = cards.slice(index, index + 4);
          const extra = cards.slice(index + 4, index + 6);
          index += page.length + extra.length;

          const columns = [
            [page[0], page[2]].filter(Boolean),
            [page[1], page[3]].filter(Boolean)
          ];

          if (extra.length) columns.push(extra);

          columns.forEach((columnCards, columnIndex) => {
            const isTail = columnIndex === columns.length - 1 && extra.length > 0;
            pushSlide({
              role: isTail ? 'tail' : 'column',
              group,
              width: isTail ? cardWidth + remainder : cardWidth,
              marginLeft: 0,
              snap: !isTail,
              rows: columnCards.map((card) => ({ cards: [card], span: 'half' }))
            });
          });

          group += 1;
          continue;
        }

        const page = cards.slice(index, index + 4);
        index += page.length;
        const rows = [];

        for (let cursor = 0; cursor < page.length; cursor += 2) {
          rows.push({ cards: page.slice(cursor, cursor + 2), span: 'half' });
        }

        pushSlide({
          role: 'grid',
          group,
          width,
          marginLeft: 0,
          snap: true,
          rows
        });
        group += 1;
      }

      return { slides, snaps };
    }

    #mountSlides(slides) {
      const existing = [...this.#stage.querySelectorAll('[data-mosaic-slide]')];

      slides.forEach((slidePlan, index) => {
        let slide = existing[index];

        if (!slide) {
          slide = document.createElement('div');
          slide.className = 'nmg__slide';
          slide.dataset.mosaicSlide = '';
          this.#stage.appendChild(slide);
        }

        slide.classList.toggle('nmg__slide--tail', slidePlan.snap === false);
        slide.style.marginLeft = slidePlan.marginLeft ? `${slidePlan.marginLeft}px` : '';
        slide.querySelector('[data-mosaic-snap]')?.remove();

        slidePlan.rows.forEach((row) => {
          row.cards.forEach((card) => slide.appendChild(card));
        });
      });

      existing.slice(slides.length).forEach((slide) => slide.remove());
    }

    #layoutBand(band, cardWidth, gap, baseHeight) {
      const rowCount = band.reduce((count, item) => Math.max(count, item.plan.rows.length), 0);
      const metrics = [];

      for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        const rowCards = band.flatMap((item) => item.plan.rows[rowIndex]?.cards || []);
        metrics.push(this.#fitHeight(rowCards, rowCards.map(() => cardWidth), baseHeight));
      }

      let bandHeight = 0;

      band.forEach((item) => {
        if (!item.el) return;
        item.el.style.width = `${item.plan.width}px`;
        bandHeight = Math.max(
          bandHeight,
          this.#placeMeasured(item.plan.rows, () => [cardWidth], 0, metrics, gap)
        );
      });

      return bandHeight;
    }

    #placeMeasured(rows, widthsFor, yStart, metrics, gap) {
      let y = yStart;

      rows.forEach((row, rowIndex) => {
        const metric = metrics[rowIndex];
        this.#placeRow(row.cards, widthsFor(row), y, metric.height, metric.labelBand, gap);
        y += metric.height;
        if (rowIndex < rows.length - 1) y += gap;
      });

      return y;
    }

    #flattenSlides() {
      const slides = [...this.#stage.querySelectorAll('[data-mosaic-slide]')];
      if (!slides.length) {
        this.#hideDots();
        return;
      }

      const cards = [...this.#stage.querySelectorAll('[data-mosaic-card]')];
      cards.forEach((card) => this.#stage.appendChild(card));
      slides.forEach((slide) => slide.remove());
      this.#stage.scrollLeft = 0;
      this.#snapPoints = [];
      this.#hideDots();
    }

    #cardStageLeft(card) {
      const slide = card.closest('[data-mosaic-slide]');
      if (!slide) return card.offsetLeft;
      return slide.offsetLeft + card.offsetLeft;
    }

    #dotsEl() {
      if (!this.#dots || !this.#dots.isConnected) {
        this.#dots = this.querySelector('[data-mosaic-dots]');
      }
      return this.#dots;
    }

    #hideDots() {
      const dots = this.#dotsEl();
      if (!dots) return;
      dots.hidden = true;
    }

    #currentSlide() {
      const points = this.#snapPoints;
      if (!points.length || !this.#stage) return 0;

      const left = this.#stage.scrollLeft;
      let index = 0;
      let best = Infinity;

      points.forEach((point, pointIndex) => {
        const distance = Math.abs(left - point);
        if (distance < best) {
          best = distance;
          index = pointIndex;
        }
      });

      return index;
    }

    #setActiveDot(index) {
      const dots = this.#dotsEl();
      if (!dots) return;

      dots.querySelectorAll('.nmg__dot').forEach((dot, dotIndex) => {
        if (dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    #scrollToSlide(index) {
      if (!this.#stage) return;
      const left = this.#snapPoints[index] ?? index * this.#stage.clientWidth;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.#stage.scrollTo({
        left,
        behavior: reduce ? 'auto' : 'smooth'
      });
    }

    #syncDots(count) {
      const dots = this.#dotsEl();
      if (!dots) return;

      if (count < 2) {
        dots.hidden = true;
        dots.replaceChildren();
        dots.dataset.count = '0';
        return;
      }

      dots.hidden = false;

      if (dots.dataset.count !== String(count)) {
        dots.dataset.count = String(count);
        dots.replaceChildren();

        for (let index = 0; index < count; index += 1) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'nmg__dot';
          button.setAttribute('aria-label', `Slayt ${index + 1}`);
          button.addEventListener('click', () => this.#scrollToSlide(index));
          dots.appendChild(button);
        }
      }

      this.#setActiveDot(this.#currentSlide());
    }

    #layoutMobile(cards, width) {
      const gap = this.#clampGap(width, this.#number('gapMobile', 10), 2);
      this.style.setProperty('--nmg-gap-mobile', `${gap}px`);
      const cardWidth = Math.floor((width - gap) / 2);
      const baseHeight = Math.round(cardWidth * (this.#number('mobileHeight', 122) / 100));
      const plan = this.#mobilePlan(cards, width, gap, cardWidth);
      const restoreIndex = this.#lastMode === 'mobile' && this.#lastWidth > 0 && this.#lastWidth !== width
        ? this.#currentSlide()
        : 0;

      this.#mountSlides(plan.slides);
      this.#snapPoints = plan.snaps;

      const slideEls = [...this.#stage.querySelectorAll('[data-mosaic-slide]')];
      let maxHeight = 0;
      let cursor = 0;

      while (cursor < plan.slides.length) {
        const slidePlan = plan.slides[cursor];
        const slide = slideEls[cursor];

        if (!slide) {
          cursor += 1;
          continue;
        }

        if (slidePlan.role === 'column' || slidePlan.role === 'tail') {
          const band = [];

          while (cursor < plan.slides.length && plan.slides[cursor].group === slidePlan.group) {
            band.push({ plan: plan.slides[cursor], el: slideEls[cursor] });
            cursor += 1;
          }

          maxHeight = Math.max(maxHeight, this.#layoutBand(band, cardWidth, gap, baseHeight));
          continue;
        }

        slide.style.width = `${slidePlan.width}px`;
        const widthsFor = (row) => (
          row.span === 'full' ? row.cards.map(() => slidePlan.width) : row.cards.map(() => cardWidth)
        );
        const metrics = slidePlan.rows.map((row) => this.#fitHeight(row.cards, widthsFor(row), baseHeight));
        maxHeight = Math.max(maxHeight, this.#placeMeasured(slidePlan.rows, widthsFor, 0, metrics, gap));
        cursor += 1;
      }

      slideEls.forEach((slide) => {
        slide.style.height = `${maxHeight}px`;
      });

      this.#stage.style.height = `${maxHeight}px`;

      if (this.#lastMode === 'mobile' && this.#lastWidth > 0 && this.#lastWidth !== width) {
        const left = this.#snapPoints[restoreIndex] ?? 0;
        this.#stage.style.scrollBehavior = 'auto';
        this.#stage.scrollLeft = left;
        this.#stage.style.scrollBehavior = '';
      }

      this.#syncDots(this.#snapPoints.length);
    }

    #layoutMosaic(cards, width) {
      const gap = this.#clampGap(width, this.#number('gapDesktop', 12), BOTTOM_PER_ROW);
      const wideWidth = this.#distribute(TOP_WEIGHTS, width, gap)[0] || width;
      const baseHeight = Math.max(1, Math.round(wideWidth * (this.#number('rowHeight', 49) / 100)));
      const top = cards.filter((card) => card.dataset.row === 'top');
      const bottom = cards.filter((card) => card.dataset.row !== 'top');
      let y = 0;

      if (top.length) {
        const weights = top.length === TOP_WEIGHTS.length ? TOP_WEIGHTS : top.map(() => 1);
        const widths = this.#distribute(weights, width, gap);
        const fitted = this.#fitHeight(top, widths, baseHeight);
        this.#placeRow(top, widths, y, fitted.height, fitted.labelBand, gap);
        y += fitted.height + (bottom.length ? gap : 0);
      }

      this.#chunk(bottom, BOTTOM_PER_ROW).forEach((row, rowIndex, allRows) => {
        const weights = row.length === BOTTOM_WEIGHTS.length ? BOTTOM_WEIGHTS : row.map(() => 1);
        const widths = this.#distribute(weights, width, gap);
        const fitted = this.#fitHeight(row, widths, baseHeight);
        this.#placeRow(row, widths, y, fitted.height, fitted.labelBand, gap);
        y += fitted.height;
        if (rowIndex < allRows.length - 1) y += gap;
      });

      this.#stage.style.height = `${Math.max(0, y)}px`;
    }
  }

  customElements.define('nodus-mosaic-grid', NodusMosaicGrid);
}
